import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sxFormError, sxLabel } from "../../../../assets/styles/CommonSxProps";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { SendEmailValidation } from "../../../../interfaces/validation/Cancelamento/SendEmailValidation";
import { enviarEmailPosVenda } from "../../../../services/ContratoService";
import { getBenef } from "../../../../Store/storages/BeneficiarioStorage";
import { cellphoneMask } from "../../../../utils/PhoneRegex";
import { sxTitle } from "../../PermissoesVisualizacao/sxProps";
import { sxButton } from "../SolicitacaoCancelamento/sxProps";
import "./styles.css";
import {
  sxBoxSendEmailForm,
  sxInputSendEmailFormFull,
  sxMessageTextField,
  sxMessageTextFieldContrast,
} from "./sxProps";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import LoadingApp from "../../../../components/LoadingApp";

const EnviarEmail: React.FC<{ goBack: () => void }> = ({ goBack }) => {
  const accessibility = useAccessibility();
  const [loading, setLoading] = useState<boolean>(false)

  const navigate = useNavigate();
  const benef = getBenef();
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const formik = useFormik({
    initialValues: {
      email: "",
      tel: "",
      mensagem: "",
    },
    validationSchema: SendEmailValidation,
    onSubmit: (values) => {
      sendEmail(values.email, values.tel, values.mensagem);
    },
    validateOnChange: false,
  });

  const sendEmail = async (
    email: string,
    telefone: string,
    mensagem: string
  ) => {
    setLoading(true)
    const emailResponse = await enviarEmailPosVenda(telefone, email, mensagem);
    if (emailResponse?.status === 200) {
      openModalSuccess(emailResponse?.data);
    } else {
      const errorMessage = !!emailResponse?.data.mensagem
        ? emailResponse.data.mensagem
        : "Tente novamente em instantes";
      openModalError("Erro", errorMessage);
      console.error(emailResponse);
    }
    setLoading(false)
  };

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: "error",
      title: title,
      subTitle: subtitle,
      textButton: "Fechar",
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null),
    });
  };

  const openModalSuccess = (mensagem: string) => {
    setModalFeedback({
      icon: "success",
      title: "Solicitação recebida!",
      subTitle: mensagem ?? "E-mail enviado com sucesso",
      textButton: "Voltar para o início",
      closeAction: () => navigate("/home"),
      buttonAction: () => navigate("/home"),
      showButtonModal: true,
    });
  };

  return (
    <>
      <div className="cancellation-container">
        <Typography style={accessibility.textStyleAccessibility} sx={sxTitle}>
          Envie um e-mail para a equipe de pós-vendas
        </Typography>
        <form onSubmit={formik.handleSubmit} className="form-change-data">
          <Box sx={sxBoxSendEmailForm}>
            <div className="send-email-form-labeled-outlined-input">
              <Typography
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                E-mail
              </Typography>
              <OutlinedInput
                type={"text"}
                value={formik.values.email}
                onChange={(e) => {
                  formik.setFieldValue("email", e.target.value);
                }}
                name="email"
                placeholder="email@email.com"
                error={!!formik.errors.email}
                maxRows={1}
                inputProps={{ maxLength: 100 }}
                sx={sxInputSendEmailFormFull}
                style={{
                  ...accessibility.contrastStyle,
                  ...accessibility.textStyleAccessibility,
                }}
              />
              {formik.errors.email && (
                <Typography
                  tabIndex={0}
                  style={accessibility.textStyleAccessibility}
                  sx={sxFormError}
                >
                  {formik.errors.email}
                </Typography>
              )}
            </div>
            <div className="send-email-form-labeled-outlined-input">
              <Typography
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                Telefone
              </Typography>
              <OutlinedInput
                type={"text"}
                value={formik.values.tel}
                onChange={(e) => {
                  formik.setFieldValue("tel", cellphoneMask(e.target.value));
                }}
                name="tel"
                placeholder="(99) 99999-9999"
                error={!!formik.errors.tel}
                maxRows={1}
                inputProps={{ maxLength: 15 }}
                sx={sxInputSendEmailFormFull}
                style={{
                  ...accessibility.contrastStyle,
                  ...accessibility.textStyleAccessibility,
                }}
              />
              {formik.errors.tel && (
                <Typography
                  tabIndex={0}
                  style={accessibility.textStyleAccessibility}
                  sx={sxFormError}
                >
                  {formik.errors.tel}
                </Typography>
              )}
            </div>
            <div className="send-email-form-labeled-outlined-input">
              <Typography
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                Mensagem
              </Typography>
              <TextField
                id="outlined-textarea"
                type="text"
                value={formik.values.mensagem}
                onChange={(e) => {
                  formik.setFieldValue("mensagem", e.target.value);
                }}
                placeholder="Descrição"
                error={!!formik.errors.mensagem}
                multiline
                sx={
                  accessibility.contrastMode
                    ? sxMessageTextFieldContrast
                    : sxMessageTextField
                }
                inputProps={{ maxLength: 500 }}
              />
              {formik.errors.mensagem && (
                <Typography tabIndex={0} sx={sxFormError}>
                  {formik.errors.mensagem}
                </Typography>
              )}
            </div>
            <Button
              variant="contained"
              size="large"
              type="submit"
              sx={sxButton}
              style={accessibility.buttonStyleAccessibility}
              disabled={
                !formik.values.email ||
                !formik.values.mensagem ||
                !formik.values.tel
              }
            >
              <Typography style={accessibility.buttonTextStyleAccessibility}>
                Enviar
              </Typography>
            </Button>
            <button
              onClick={goBack}
              className="back-button"
              style={accessibility.textStyleAccessibility}
            >
              <FontAwesomeIcon
                style={accessibility.textStyleAccessibility}
                icon={faChevronLeft}
              />
              &nbsp;&nbsp;<u>Voltar</u>
            </button>
          </Box>
        </form>
      </div>
      <LoadingApp showLoading={loading} />
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
        classNameModal={`modal-custom ${modalFeedback?.icon === 'error' ? "error" : ''}`}
      />
    </>
  );
};

export default EnviarEmail;
